/**
 * Общие куски формы универсального скринера (PULSE-268):
 * блок метрики (вкл/направление/окно/порог), конвертация форма<->params.
 * Используются страницей /app/screeners (настройки инстанса),
 * /app/screeners/:id (параметры бектеста) и «Своим вариантом» подбора.
 *
 * Дельта тейкеров (ключ nd; не Net Delta CoinGlass) умеет два режима порога (PULSE-268): '%' (доля дисбаланса,
 * монето-независимо, дефолт) и 'USDT' (абсолютный поток за окно). Цена
 * и OI — всегда %.
 *
 * PULSE-371: цена умеет «стоит» (|изм.| <= порога — для дивергенции с OI),
 * метрика «Гап» (1-мин свеча >= порога за последние N мин), окна до 240 мин,
 * пороги от 0.1%, пресеты типовых сетапов.
 *
 * PULSE-379: условия «Объём» (×N к обычному), «Ликвидации» ($ за окно),
 * «Фандинг» (%) и блок «Фильтры монет и рынка» (params.filters).
 */
import type { ReactNode } from 'react'

import type { MetricParams, UniversalParams } from '@/hooks/useScreenersApi'
import { cn } from '@/lib/cn'

export const INPUT =
  'w-full bg-surface border border-line px-2.5 py-1.5 text-sm text-text outline-none focus:border-pulse/50'

export function Field({ label, hint, children }: {
  label: string; hint?: string; children: ReactNode
}) {
  return (
    <div>
      <label className="block text-xs text-textFaint mb-1">{label}</label>
      {children}
      {hint && <p className="text-2xs text-textFaint mt-1 leading-snug">{hint}</p>}
    </div>
  )
}

export interface MetricForm {
  enabled: boolean
  direction: string
  window_min: string
  change_pct: string
  // nd (режим USDT) и liq: порог в USDT
  mode: 'pct' | 'usd'
  threshold_usd: string
}

export interface FilterForm {
  min_turnover_m: string
  min_oi_m: string
  symbols_only: string
  symbols_exclude: string
  btc_enabled: boolean
  btc_direction: string
  btc_window: string
  btc_pct: string
  hours_enabled: boolean
  hours_from: string
  hours_to: string
}

export const METRIC_KEYS = ['price', 'oi', 'nd', 'gap', 'vol', 'liq', 'fund'] as const
export type MetricKey = (typeof METRIC_KEYS)[number]

export type MetricForms = Record<MetricKey, MetricForm> & { filters: FilterForm }

export const METRIC_TITLE: Record<MetricKey, string> = {
  price: 'Цена', oi: 'Открытый интерес (OI)', nd: 'Дельта тейкеров (покупки против продаж)',
  gap: 'Гап (резкая свеча за 1 мин)', vol: 'Всплеск объёма', liq: 'Ликвидации', fund: 'Фандинг',
}

const METRIC_HINT: Partial<Record<MetricKey, string>> = {
  oi: 'Изменение суммарного OI по биржам в монетах (как CoinGlass «Coins»), цена в нём не учитывается.',
  nd: 'Перевес рыночных покупок над продажами (тейкеры Binance). Это не «Net Delta» CoinGlass — та строится по OI.',
  vol: 'Оборот за окно во столько раз выше обычного (среднее за прошлые сутки).',
  liq: 'Сумма ликвидаций за окно (Binance).',
  fund: 'Текущая ставка фандинга (Bybit). Обычно около 0,01%.',
}

export const METRIC_DIR_LABELS: Record<MetricKey, string[]> = {
  price: ['Растёт', 'Падает', 'Стоит (±%)'],
  oi: ['Растёт', 'Падает'],
  nd: ['Перевес покупок', 'Перевес продаж'],
  gap: ['Вверх', 'Вниз'],
  vol: ['Выше обычного'],
  liq: ['Лонгов', 'Шортов', 'Всех'],
  fund: ['Положительный, не меньше', 'Отрицательный, не меньше по модулю'],
}

const DIR_VALUES: Record<MetricKey, string[]> = {
  price: ['pump', 'dump', 'flat'], oi: ['up', 'down'], nd: ['up', 'down'], gap: ['up', 'down'],
  vol: ['up'], liq: ['long', 'short', 'any'], fund: ['pos', 'neg'],
}

const WINDOW_MAX: Record<MetricKey, number> = {
  price: 240, oi: 240, nd: 240, gap: 60, vol: 240, liq: 240, fund: 1,
}
// порог в % (для vol — «во сколько раз», для fund — % ставки)
const PCT_RANGE: Partial<Record<MetricKey, [number, number]>> = { vol: [1.1, 100], fund: [0.001, 5] }

const DEFAULTS: Partial<Record<MetricKey, Partial<MetricForm>>> = {
  vol: { change_pct: '5', window_min: '15' },
  liq: { threshold_usd: '500000', window_min: '15' },
  fund: { change_pct: '0.05', window_min: '1' },
}

export const EMPTY_FILTERS: FilterForm = {
  min_turnover_m: '', min_oi_m: '', symbols_only: '', symbols_exclude: '',
  btc_enabled: false, btc_direction: 'up', btc_window: '60', btc_pct: '0.5',
  hours_enabled: false, hours_from: '9', hours_to: '21',
}

const off = (k: MetricKey): MetricForm => ({
  enabled: false, direction: DIR_VALUES[k][0], window_min: '15', change_pct: '10',
  mode: 'pct', threshold_usd: '100000', ...DEFAULTS[k],
})
const on = (k: MetricKey, direction: string, window_min: number, value: number): MetricForm => ({
  ...off(k), enabled: true, direction, window_min: String(window_min),
  ...(k === 'liq' ? { threshold_usd: String(value) } : { change_pct: String(value) }),
})
/** Полный набор форм: указанные метрики включены, остальные выключены. */
const forms = (part: Partial<Record<MetricKey, MetricForm>>): MetricForms => ({
  ...(Object.fromEntries(METRIC_KEYS.map((k) => [k, part[k] ?? off(k)])) as Record<MetricKey, MetricForm>),
  filters: EMPTY_FILTERS,
})

/** Типовые сетапы — заполняют форму, дальше пороги правятся руками. */
export const PRESETS: { label: string; hint: string; forms: MetricForms }[] = [
  {
    label: 'Цена↓ OI↑', hint: 'дивергенция: цена падает, OI растёт (набор шортов / лонги на дне)',
    forms: forms({ price: on('price', 'dump', 60, 3), oi: on('oi', 'up', 60, 5) }),
  },
  {
    label: 'Цена↑ OI↓', hint: 'дивергенция: цена растёт, OI падает (рост на закрытии шортов)',
    forms: forms({ price: on('price', 'pump', 60, 3), oi: on('oi', 'down', 60, 5) }),
  },
  {
    label: 'Цена стоит, OI↑', hint: 'цена в пределах ±2% за час, OI вырос на 20%+',
    forms: forms({ price: on('price', 'flat', 60, 2), oi: on('oi', 'up', 60, 20) }),
  },
  {
    label: 'Гап вверх', hint: '1-мин свеча +3% и больше за последние 5 мин',
    forms: forms({ gap: on('gap', 'up', 5, 3) }),
  },
  {
    label: 'Гап вниз', hint: '1-мин свеча −3% и больше за последние 5 мин',
    forms: forms({ gap: on('gap', 'down', 5, 3) }),
  },
  {
    label: 'Всплеск объёма', hint: 'оборот за 15 мин в 5 раз выше обычного',
    forms: forms({ vol: on('vol', 'up', 15, 5) }),
  },
  {
    label: 'Вынос лонгов', hint: 'цена −5% за 30 мин и ликвидации лонгов от $500k за 15 мин',
    forms: forms({ price: on('price', 'dump', 30, 5), liq: on('liq', 'long', 15, 500000) }),
  },
]

export function metricFormsFromParams(params: UniversalParams | undefined): MetricForms {
  const one = (k: MetricKey): MetricForm => {
    const m = ((params ?? {})[k] ?? {}) as MetricParams
    const d = off(k)
    return {
      enabled: !!m.enabled,
      direction: m.direction ?? d.direction,
      window_min: m.window_min != null ? String(m.window_min) : d.window_min,
      change_pct: m.change_pct != null ? String(m.change_pct) : d.change_pct,
      mode: m.mode === 'usd' ? 'usd' : 'pct',
      threshold_usd: m.threshold_usd != null ? String(m.threshold_usd) : d.threshold_usd,
    }
  }
  const f = (params?.filters ?? {}) as NonNullable<UniversalParams['filters']>
  return {
    ...(Object.fromEntries(METRIC_KEYS.map((k) => [k, one(k)])) as Record<MetricKey, MetricForm>),
    filters: {
      min_turnover_m: f.min_turnover_m != null ? String(f.min_turnover_m) : '',
      min_oi_m: f.min_oi_m != null ? String(f.min_oi_m) : '',
      symbols_only: (f.symbols_only ?? []).join(', '),
      symbols_exclude: (f.symbols_exclude ?? []).join(', '),
      btc_enabled: !!f.btc?.enabled,
      btc_direction: f.btc?.direction ?? 'up',
      btc_window: String(f.btc?.window_min ?? 60),
      btc_pct: String(f.btc?.change_pct ?? 0.5),
      hours_enabled: !!f.hours?.enabled,
      hours_from: String(f.hours?.from ?? 9),
      hours_to: String(f.hours?.to ?? 21),
    },
  }
}

const numIn = (s: string) => Number(s.replace(',', '.'))
const symList = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim().toUpperCase()).filter(Boolean)

/** Форма -> params для API. Возвращает строку-ошибку, если поля невалидны. */
export function metricsToParams(f: MetricForms): UniversalParams | string {
  const params: UniversalParams = {}
  let any = false
  for (const k of METRIC_KEYS) {
    const m = f[k]
    if (!m.enabled) { params[k] = { enabled: false }; continue }
    const w = k === 'fund' ? 1 : Number(m.window_min)
    if (!Number.isInteger(w) || w < 1 || w > WINDOW_MAX[k]) {
      return `${METRIC_TITLE[k]}: окно — целое число минут от 1 до ${WINDOW_MAX[k]}`
    }
    const dir = m.direction as MetricParams['direction']
    if (k === 'liq' || (k === 'nd' && m.mode === 'usd')) {
      const t = numIn(m.threshold_usd)
      if (Number.isNaN(t) || t <= 0) return `${METRIC_TITLE[k]}: сумма в USDT — положительное число`
      params[k] = {
        enabled: true, direction: dir, window_min: w, threshold_usd: t,
        ...(k === 'nd' ? { mode: 'usd' as const } : {}),
      }
    } else {
      const p = numIn(m.change_pct)
      const [lo, hi] = PCT_RANGE[k] ?? [0.1, 100]
      if (Number.isNaN(p) || p < lo || p > hi) {
        return `${METRIC_TITLE[k]}: значение от ${String(lo).replace('.', ',')} до ${hi}`
      }
      params[k] = {
        enabled: true, direction: dir, window_min: w, change_pct: p,
        ...(k === 'nd' ? { mode: 'pct' as const } : {}),
      }
    }
    any = true
  }
  if (!any) return 'Включите хотя бы одно условие'
  const ff = f.filters
  const filters: NonNullable<UniversalParams['filters']> = {}
  for (const [key, title] of [['min_turnover_m', 'Мин. оборот'], ['min_oi_m', 'Мин. OI']] as const) {
    if (ff[key].trim()) {
      const v = numIn(ff[key])
      if (Number.isNaN(v) || v <= 0) return `${title}: положительное число (млн $)`
      filters[key] = v
    }
  }
  if (symList(ff.symbols_only).length) filters.symbols_only = symList(ff.symbols_only)
  if (symList(ff.symbols_exclude).length) filters.symbols_exclude = symList(ff.symbols_exclude)
  if (ff.btc_enabled) {
    const w = Number(ff.btc_window), p = numIn(ff.btc_pct)
    if (!Number.isInteger(w) || w < 1 || w > 240) return 'BTC: окно — от 1 до 240 мин'
    if (Number.isNaN(p) || p < 0.1 || p > 100) return 'BTC: значение от 0,1 до 100%'
    filters.btc = { enabled: true, direction: ff.btc_direction, window_min: w, change_pct: p }
  }
  if (ff.hours_enabled) {
    const a = Number(ff.hours_from), b = Number(ff.hours_to)
    if (a === b) return 'Часы: «с» и «до» не должны совпадать'
    filters.hours = { enabled: true, from: a, to: b }
  }
  if (Object.keys(filters).length) params.filters = filters
  return params
}

export function MetricBlock({ metric, value, onChange }: {
  metric: MetricKey
  value: MetricForm
  onChange: (m: MetricForm) => void
}) {
  const dirValues = DIR_VALUES[metric]
  const dirLabels = METRIC_DIR_LABELS[metric]
  const isNd = metric === 'nd'
  const usd = metric === 'liq' || (isNd && value.mode === 'usd')
  const hasWindow = metric !== 'fund'
  const hasDir = dirValues.length > 1
  return (
    <div className={cn('border p-3', value.enabled ? 'border-pulse/30 bg-pulse/5' : 'border-line')}>
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          />
          {METRIC_TITLE[metric]}
        </label>
        {isNd && value.enabled && (
          <div className="flex border border-line text-2xs">
            {(['pct', 'usd'] as const).map((mo) => (
              <button
                key={mo}
                type="button"
                onClick={() => onChange({ ...value, mode: mo })}
                className={cn('px-2 py-0.5',
                  value.mode === mo ? 'bg-line/50 text-text' : 'text-textDim hover:text-text')}
              >
                {mo === 'pct' ? '% дисбаланс' : 'USDT поток'}
              </button>
            ))}
          </div>
        )}
      </div>
      {value.enabled && METRIC_HINT[metric] && (
        <p className="text-2xs text-textFaint -mt-1 mb-2 leading-snug">{METRIC_HINT[metric]}</p>
      )}
      {value.enabled && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {hasDir && (
            <div className="col-span-2 sm:col-span-1"><Field label={metric === 'liq' ? 'Чьи' : 'Направление'}>
              <select
                value={value.direction}
                onChange={(e) => onChange({ ...value, direction: e.target.value })}
                className={INPUT}
              >
                {dirValues.map((v, i) => <option key={v} value={v}>{dirLabels[i]}</option>)}
              </select>
            </Field></div>
          )}
          {hasWindow && (
            <Field label={metric === 'gap'
              ? `За последние, мин (до ${WINDOW_MAX[metric]})`
              : `Окно, мин (до ${WINDOW_MAX[metric]})`}>
              <input
                value={value.window_min}
                onChange={(e) => onChange({ ...value, window_min: e.target.value })}
                className={INPUT} inputMode="numeric" placeholder="15"
              />
            </Field>
          )}
          {usd ? (
            <Field label={metric === 'liq' ? 'Сумма, USDT' : 'Поток, USDT'}>
              <input
                value={value.threshold_usd}
                onChange={(e) => onChange({ ...value, threshold_usd: e.target.value })}
                className={INPUT} inputMode="numeric" placeholder="500000"
              />
            </Field>
          ) : (
            <Field label={metric === 'gap' ? 'Свеча за 1 мин, %'
              : metric === 'vol' ? 'Во сколько раз'
              : metric === 'fund' ? 'Ставка, %'
              : metric === 'price' && value.direction === 'flat' ? 'Не больше ±%'
              : 'На сколько, %'}>
              <input
                value={value.change_pct}
                onChange={(e) => onChange({ ...value, change_pct: e.target.value })}
                className={INPUT} inputMode="decimal" placeholder={metric === 'fund' ? '0,05' : '10'}
              />
            </Field>
          )}
        </div>
      )}
    </div>
  )
}

const HOURS = Array.from({ length: 24 }, (_, h) => h)

/** Фильтры монет и рынка: применяются поверх условий. */
export function FiltersBlock({ value, onChange }: {
  value: FilterForm
  onChange: (f: FilterForm) => void
}) {
  const set = (p: Partial<FilterForm>) => onChange({ ...value, ...p })
  const count = [value.min_turnover_m.trim(), value.min_oi_m.trim(), value.symbols_only.trim(),
    value.symbols_exclude.trim(), value.btc_enabled, value.hours_enabled].filter(Boolean).length
  return (
    <details className="border border-line" open={count > 0 || undefined}>
      <summary className="px-3 py-2.5 text-sm font-medium cursor-pointer select-none">
        Фильтры монет и рынка
        <span className="text-xs text-textFaint font-normal">
          {count ? ` · включено ${count}` : ' · необязательно'}
        </span>
      </summary>
      <div className="px-3 pb-3 space-y-3">
        <p className="text-2xs text-textFaint leading-snug">
          Сигнал придёт, только если монета и рынок подходят под эти фильтры. Пустое поле — без ограничения.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Оборот 24ч от, млн $">
            <input value={value.min_turnover_m} onChange={(e) => set({ min_turnover_m: e.target.value })}
              className={INPUT} inputMode="decimal" placeholder="например 20" />
          </Field>
          <Field label="OI от, млн $">
            <input value={value.min_oi_m} onChange={(e) => set({ min_oi_m: e.target.value })}
              className={INPUT} inputMode="decimal" placeholder="например 5" />
          </Field>
        </div>
        <Field label="Только эти монеты" hint="Через запятую: SOL, PEPE, WIF. USDT допишется сам.">
          <input value={value.symbols_only} onChange={(e) => set({ symbols_only: e.target.value })}
            className={INPUT} placeholder="все монеты" />
        </Field>
        <Field label="Исключить монеты">
          <input value={value.symbols_exclude} onChange={(e) => set({ symbols_exclude: e.target.value })}
            className={INPUT} placeholder="например BTC, ETH" />
        </Field>

        <div className={cn('border p-2.5', value.btc_enabled ? 'border-pulse/30 bg-pulse/5' : 'border-line/60')}>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={value.btc_enabled} onChange={(e) => set({ btc_enabled: e.target.checked })} />
            Только когда BTC…
          </label>
          {value.btc_enabled && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              <div className="col-span-2 sm:col-span-1"><Field label="BTC">
                <select value={value.btc_direction} onChange={(e) => set({ btc_direction: e.target.value })} className={INPUT}>
                  <option value="up">растёт</option>
                  <option value="down">падает</option>
                  <option value="flat">стоит (±%)</option>
                </select>
              </Field></div>
              <Field label="Окно, мин">
                <input value={value.btc_window} onChange={(e) => set({ btc_window: e.target.value })}
                  className={INPUT} inputMode="numeric" />
              </Field>
              <Field label={value.btc_direction === 'flat' ? 'Не больше ±%' : 'На сколько, %'}>
                <input value={value.btc_pct} onChange={(e) => set({ btc_pct: e.target.value })}
                  className={INPUT} inputMode="decimal" />
              </Field>
            </div>
          )}
        </div>

        <div className={cn('border p-2.5', value.hours_enabled ? 'border-pulse/30 bg-pulse/5' : 'border-line/60')}>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={value.hours_enabled} onChange={(e) => set({ hours_enabled: e.target.checked })} />
            Только в эти часы (МСК)
          </label>
          {value.hours_enabled && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Field label="С">
                <select value={value.hours_from} onChange={(e) => set({ hours_from: e.target.value })} className={INPUT}>
                  {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                </select>
              </Field>
              <Field label="До">
                <select value={value.hours_to} onChange={(e) => set({ hours_to: e.target.value })} className={INPUT}>
                  {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                </select>
              </Field>
            </div>
          )}
        </div>
      </div>
    </details>
  )
}

const num = (v: unknown) => String(Number(v)).replace('.', ',')

/** Имя скринера из условий — то же правило, что auto_name() в
 *  universal_screener.py (заголовок сообщения в Telegram). Метрика без
 *  `enabled` считается включённой (так хранятся снапшоты сигналов). */
export function autoName(params: UniversalParams | Record<string, unknown> | undefined): string {
  const get = (k: MetricKey) => {
    const m = (params ?? {})[k] as MetricParams | undefined
    return m && m.enabled !== false && m.direction ? m : undefined
  }
  const parts: string[] = []
  const p = get('price')
  if (p) {
    const pre = p.direction === 'pump' ? 'Памп +' : p.direction === 'dump' ? 'Дамп −' : 'Цена стоит ±'
    parts.push(`${pre}${num(p.change_pct)}%/${p.window_min}м`)
  }
  const o = get('oi')
  if (o) parts.push(`OI ${o.direction === 'up' ? '+' : '−'}${num(o.change_pct)}%/${o.window_min}м`)
  const n = get('nd')
  if (n) {
    const thr = n.mode === 'usd' ? `$${num(Number(n.threshold_usd) / 1000)}k` : `${num(n.change_pct)}%`
    parts.push(`${n.direction === 'up' ? 'Покупки' : 'Продажи'} ${thr}/${n.window_min}м`)
  }
  const g = get('gap')
  if (g) {
    parts.push(`Гап ${g.direction === 'up' ? '+' : '−'}${num(g.change_pct)}%` +
      (g.window_min === 1 ? '' : ` за ${g.window_min}м`))
  }
  const v = get('vol')
  if (v) parts.push(`Объём ×${num(v.change_pct)}/${v.window_min}м`)
  const l = get('liq')
  if (l) {
    const who = l.direction === 'long' ? 'Ликв. лонгов' : l.direction === 'short' ? 'Ликв. шортов' : 'Ликвидации'
    parts.push(`${who} $${num(Number(l.threshold_usd) / 1000)}k/${l.window_min}м`)
  }
  const fu = get('fund')
  if (fu) parts.push(`Фандинг ${fu.direction === 'pos' ? '≥+' : '≤−'}${num(fu.change_pct)}%`)
  return parts.join(' + ') || 'Скринер'
}

/** Фильтры обычным языком — строка под условиями (пусто, если фильтров нет). */
export function filtersSummary(params: UniversalParams | Record<string, unknown> | undefined): string {
  const f = (params as UniversalParams | undefined)?.filters
  if (!f) return ''
  const parts: string[] = []
  if (f.min_turnover_m) parts.push(`оборот 24ч от $${num(f.min_turnover_m)}M`)
  if (f.min_oi_m) parts.push(`OI от $${num(f.min_oi_m)}M`)
  const short = (xs: string[]) => xs.map((x) => x.replace(/USDT$/, '')).join(', ')
  if (f.symbols_only?.length) parts.push(`только ${short(f.symbols_only)}`)
  if (f.symbols_exclude?.length) parts.push(`без ${short(f.symbols_exclude)}`)
  if (f.btc?.enabled) {
    const d = f.btc.direction === 'up' ? `растёт на ${num(f.btc.change_pct)}%+`
      : f.btc.direction === 'down' ? `падает на ${num(f.btc.change_pct)}%+` : `стоит ±${num(f.btc.change_pct)}%`
    parts.push(`BTC за ${f.btc.window_min} мин ${d}`)
  }
  if (f.hours?.enabled) parts.push(`${String(f.hours.from).padStart(2, '0')}–${String(f.hours.to).padStart(2, '0')} МСК`)
  return parts.join(' · ')
}

/** Все блоки метрик + пресеты + фильтры — общий кусок формы инстанса и бектеста. */
export function MetricBlocks({ value, onChange }: {
  value: MetricForms
  onChange: (f: MetricForms) => void
}) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs text-textFaint mb-1">Готовые сетапы</p>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.label} type="button" title={p.hint}
              onClick={() => onChange({ ...p.forms, filters: value.filters })}
              className="px-2 py-0.5 text-2xs border border-line text-textDim hover:text-text hover:border-pulse/50"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {METRIC_KEYS.map((k) => (
        <MetricBlock key={k} metric={k} value={value[k]}
          onChange={(m) => onChange({ ...value, [k]: m })} />
      ))}
      <FiltersBlock value={value.filters} onChange={(filters) => onChange({ ...value, filters })} />
    </div>
  )
}
